import {
    FGTabs,
    FGTabPanel,
    FGTile,
    FGStack,
    FGStackItem,
    FGTypography,
} from '@fg/design-system';
import {
    IActiveDimensionTree,
    IAPIActiveClientFacingIndicator,
    E_RISK_TYPE,
} from 'interfaces';
import { sortBy, filter, map, flow, values } from 'lodash/fp';
import { inject } from 'mobx-react';
import { useState } from 'react';
import rootStore, { IMobxRootState } from 'RootStore';
import { RiskPickerTreeItem, RiskPickerItem } from './RiskPicker';
import RiskPickerList from './RiskPickerList';
import RiskPickerTree from './RiskPickerTree';
import RiskPickerHeader from './RiskPickerHeader';
import RiskPickerSearch from './RiskPickerSearch';

type RiskPickerIndicatorsProps = {
    selectedRisks?: number[];
    multi?: boolean;
    dimensionsTree?: IActiveDimensionTree;
    activeClientFacingIndicators?: IAPIActiveClientFacingIndicator;
    onClose: ()=>void;
    currentRiskType?: E_RISK_TYPE;
};

export const RiskPickerIndicators = inject(
    ({ risksStore }: IMobxRootState) => {
        return {
            selectedRisks: risksStore.currentList,
            currentRiskType: risksStore.currentRisksType,
            dimensionsTree: risksStore.dimensions.tree,
            activeClientFacingIndicators: risksStore.clientFacingIndicators,
        };
    }
)(
    ({
        selectedRisks,
        dimensionsTree,
        activeClientFacingIndicators,
        multi,
        onClose,
        currentRiskType,
    }: RiskPickerIndicatorsProps) => {
        const [tabIndex, setTabIndex] = useState(
            currentRiskType === E_RISK_TYPE.CLIENT_FACING_INDICATORS ? 1 : 0
        );
        const [searchValue, setSearchValue] = useState('');
        const normalizedSearch = searchValue.trim().toLowerCase();

        const matchesSearch = (name?: string): boolean =>
            (name ?? '').toLowerCase().includes(normalizedSearch);

        const filterFocusItems = (
            items: RiskPickerItem[]
        ): RiskPickerItem[] => {
            if (!normalizedSearch) {
                return items;
            }
            let currentDivider: string;
            let lastEmittedDivider: string;
            const result: RiskPickerItem[] = [];
            items.forEach(item => {
                if (item.topDivider) {
                    currentDivider = item.topDivider;
                }
                if (!matchesSearch(item.name)) {
                    return;
                }
                const showDivider =
                    !!currentDivider && currentDivider !== lastEmittedDivider;
                if (showDivider) {
                    lastEmittedDivider = currentDivider;
                }
                result.push({
                    ...item,
                    topDivider: showDivider && currentDivider,
                });
            });
            return result;
        };

        const filterFundamentalsItems = (
            tree: RiskPickerTreeItem
        ): RiskPickerItem[] => {
            const results: RiskPickerItem[] = [];
            const seenIds = new Set<number>();
            const walk = (node: RiskPickerTreeItem) => {
                if (node.hidden) {
                    return;
                }
                if (matchesSearch(node.name) && !seenIds.has(node.id)) {
                    seenIds.add(node.id);
                    const { children, ...risk } = node;
                    results.push(risk);
                }
                (node.children ?? []).forEach(child =>
                    walk(child as RiskPickerTreeItem)
                );
            };
            walk(tree);
            return results;
        };

        const renderNoResults = () => (
            <div style={{ padding: 16, textAlign: 'center' }}>
                <FGTypography
                    tag="label"
                    variant="body3-short"
                    color="weak">
                    No risk types found
                </FGTypography>
            </div>
        );

        //todo: memo()
        const recursivelyBuildFundamentalTreeMenuItem = (
            dimTree: IActiveDimensionTree
        ): RiskPickerTreeItem => {
            return {
                name: dimTree.lname,
                id: dimTree.id,
                children: dimTree.children
                    ? dimTree.children.map(childDimTree =>
                          recursivelyBuildFundamentalTreeMenuItem(childDimTree)
                      )
                    : null,
                disabled: rootStore.risksStore.checkIfRiskDisabled(
                    E_RISK_TYPE.DIMENSIONS,
                    [dimTree.id]
                ),
                hidden: dimTree.hidden,
                definition: dimTree.definition,
            };
        };

        //todo: memo()
        const getFundamentalsTreeItems = (): RiskPickerTreeItem => {
            return recursivelyBuildFundamentalTreeMenuItem(dimensionsTree);
        };

        //todo: memo()
        const getFocusListItems = (): RiskPickerItem[] => {
            return flow(
                values,
                sortBy(indicator => indicator.order),
                filter(indicator => !indicator.disabled),
                map(indicator => {
                    return {
                        id: indicator.id,
                        name: indicator.display_name,
                        iconName: `gqi-dimension${indicator.id}`,
                        disabled: rootStore.risksStore.checkIfRiskDisabled(
                            E_RISK_TYPE.CLIENT_FACING_INDICATORS,
                            [indicator.id]
                        ),
                        //todo: remove once BE returns labels
                        topDivider: indicator.top_divider,
                        definition: indicator.definition,
                    };
                })
            )(activeClientFacingIndicators);
        };

        //todo: memo()
        const onItemClick = (riskId: number) => {
            const store = rootStore.risksStore;
            const clickedRiskType =
                tabIndex === 1
                    ? E_RISK_TYPE.CLIENT_FACING_INDICATORS
                    : E_RISK_TYPE.DIMENSIONS;

            // Cross-type pick: clear the other type, switch, and start fresh 
            // stores are pre-seeded with a default, so toggling would keep it
            if (clickedRiskType !== store.currentRisksType) {
                store.currentStore.clean();
                store.setCurrentRiskType(clickedRiskType);
                store.currentStore.overrideValues(riskId);
                return;
            }
            if (multi) {
                const { currentStore } = rootStore.risksStore;
                const selectedRiskIds = rootStore.risksStore.currentList;
                const isOnlySelectedRisk =
                    selectedRiskIds.length === 1 &&
                    selectedRiskIds[0] === riskId;
                if (isOnlySelectedRisk) {
                    currentStore.overrideValues(
                        currentStore.getDefaultValue()
                    );
                    return;
                }
                rootStore.risksStore.toggleRisk(riskId);
            } else {
                store.currentStore.overrideValues(riskId);
            }
        };
        const fundamentalsSelectedIds =
            currentRiskType === E_RISK_TYPE.DIMENSIONS ? selectedRisks : [];
        const focusSelectedIds =
            currentRiskType === E_RISK_TYPE.CLIENT_FACING_INDICATORS
                ? selectedRisks
                : [];

        const filteredFundamentalsItems = normalizedSearch
            ? filterFundamentalsItems(getFundamentalsTreeItems())
            : [];
        const filteredFocusItems = filterFocusItems(getFocusListItems());

        return (
            <FGStack className="new-risk-picker-container">
                <FGTile
                    padding="small"
                    appearance="secondary"
                    fullHeight
                    className="new-risk-picker-container-tile">
                    <div
                        style={{
                            position: 'absolute',
                            left: 16,
                            right: 16,
                            top: 0,
                            bottom: 0,
                        }}>
                        <FGStack direction="column" style={{ height: '100%' }}>
                            <FGStackItem>
                                <RiskPickerHeader onClose={onClose} />
                            </FGStackItem>
                            <FGStackItem
                                style={{ flex: 1 }}
                                className="new-risk-picker-container-tabs">
                                <FGTabs
                                    variant="tertiary"
                                    tabs={[
                                        {
                                            label: 'Fundamentals',
                                        },
                                        {
                                            label: 'Focus',
                                        },
                                    ]}
                                    onClick={setTabIndex}
                                    tabIndex={tabIndex}>
                                    <FGTabPanel className="new-risk-picker-tab-panel">
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                right: 2,
                                                top: 0,
                                                bottom: 0,
                                            }}>
                                            <div className="new-risk-picker-panel-search">
                                                <RiskPickerSearch
                                                    id="risk-picker-search-fundamentals"
                                                    value={searchValue}
                                                    onChange={setSearchValue}
                                                    autoFocus={tabIndex === 0}
                                                />
                                            </div>
                                            {normalizedSearch ? (
                                                filteredFundamentalsItems.length >
                                                0 ? (
                                                    <RiskPickerList
                                                        onItemClick={onItemClick}
                                                        items={
                                                            filteredFundamentalsItems
                                                        }
                                                        multi={multi}
                                                        selectedItemsIds={fundamentalsSelectedIds}
                                                    />
                                                ) : (
                                                    renderNoResults()
                                                )
                                            ) : (
                                                <RiskPickerTree
                                                    onItemClick={onItemClick}
                                                    treeMenuItem={getFundamentalsTreeItems()}
                                                    selectedItemsIds={fundamentalsSelectedIds}
                                                    multi={multi}
                                                />
                                            )}
                                        </div>
                                    </FGTabPanel>
                                    <FGTabPanel className="new-risk-picker-tab-panel">
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                right: 2,
                                                top: 0,
                                                bottom: 0,
                                            }}>
                                            <div className="new-risk-picker-panel-search">
                                                <RiskPickerSearch
                                                    id="risk-picker-search-focus"
                                                    value={searchValue}
                                                    onChange={setSearchValue}
                                                    autoFocus={tabIndex === 1}
                                                />
                                            </div>
                                            {filteredFocusItems.length > 0 ? (
                                                <RiskPickerList
                                                    onItemClick={onItemClick}
                                                    items={filteredFocusItems}
                                                    multi={multi}
                                                    selectedItemsIds={focusSelectedIds}
                                                />
                                            ) : (
                                                renderNoResults()
                                            )}
                                        </div>
                                    </FGTabPanel>
                                </FGTabs>
                            </FGStackItem>
                        </FGStack>
                    </div>
                </FGTile>
            </FGStack>
        );
    }
);
